import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  getCancellationSurveyExit,
  type ICancellationSurveyOrigin,
  useSubmitCancellationFeedback,
} from '~/domain/JobCancellation';
import { IJob } from '~/models';
import {
  firePermInterviewCancellationReasonSubmitted,
  fireShiftCancellationReasonSubmitted,
} from '~/posthogs/events';
import { ICancellationSurveyAnswer } from './useCancellationSurveyForm';

interface IUseSubmitCancellationSurveyParams {
  jobId?: string;
  job?: IJob;
}

/**
 * Same events as today's `CancellationFeedback`. A shift goes back to where the cancel started
 * (`cancelOrigin`, set by `useCancelJobMutation`); an interview keeps today's job overview.
 * A path exit uses `replace` so Back can't reopen an answered survey.
 * A failure is shown in the dialog (`hasSubmitError`), which clears when the next submit starts.
 */
export const useSubmitCancellationSurvey = ({ jobId, job }: IUseSubmitCancellationSurveyParams) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const origin = (state as { cancelOrigin?: Partial<ICancellationSurveyOrigin> } | null)
    ?.cancelOrigin;
  const { mutate, isPending, isError } = useSubmitCancellationFeedback();

  const submit = useCallback(
    ({ reason, otherText }: ICancellationSurveyAnswer) => {
      if (!jobId || !job) return;

      mutate(
        { jobId, cancellationFeedback: reason.code, cancellationReason: otherText },
        {
          onSuccess: () => {
            if (job.isOnsiteInterview) {
              firePermInterviewCancellationReasonSubmitted({
                officeId: job.offer_owner?.id,
                onsiteInterviewId: job.id,
                postingId: job.postingId,
                reason: reason.name,
                othersReason: otherText,
              });
              navigate(`/permanent-jobs/job-overview/${job.postingId}`, { replace: true });
              return;
            }

            fireShiftCancellationReasonSubmitted({
              officeId: job.offer_owner?.id,
              shiftId: job.id,
              reason: reason.name,
              othersReason: otherText,
            });
            const exit = getCancellationSurveyExit(origin);
            if ('delta' in exit) navigate(exit.delta);
            else navigate(exit.to, { replace: true });
          },
        },
      );
    },
    [job, jobId, mutate, navigate, origin],
  );

  return { submit, isSubmitting: isPending, hasSubmitError: isError };
};
